import React, { useEffect, useState } from "react";
import { api, API, codeClasses } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, FilePdf, Clock } from "@phosphor-icons/react";

function waitMinutes(arrival) {
  const a = arrival?.ora_accettazione;
  const p = arrival?.ora_presa;
  if (!a || !p) return null;
  const parse = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
  let d = parse(p) - parse(a);
  if (d < 0) d += 24 * 60;
  return d;
}

const WAIT_THRESHOLDS = { rosso: 5, arancione: 10, azzurro: 30 };

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async (query = q) => {
    const { data } = await api.get("/triages", { params: query ? { q: query } : {} });
    setItems(data);
  };
  useEffect(() => { load(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div>
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="font-heading text-xl font-semibold">Storico triage</div>
        <div className="text-xs text-slate-500">Ricerca per paziente, protocollo, note infermiere, CF, accesso PS, infermiere</div>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Cerca per cognome, nome, CF, accesso PS, protocollo, note, infermiere…" className="pl-10 h-12" />
          </div>
          <Button onClick={load} data-testid="history-search-button" className="min-h-[48px]">Cerca</Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Paziente</th>
                <th className="text-left p-3">Accesso PS</th>
                <th className="text-left p-3">Protocollo</th>
                <th className="text-left p-3">Suggerito</th>
                <th className="text-left p-3">Assegnato</th>
                <th className="text-left p-3">Attesa</th>
                <th className="text-left p-3">Infermiere</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.triage_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs text-slate-600">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3">
                    <div>{t.patient?.cognome} {t.patient?.nome}</div>
                    {t.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1 max-w-xs" title={t.notes}>{t.notes}</div>}
                  </td>
                  <td className="p-3 font-mono text-xs">{t.patient?.accesso_ps || "—"}</td>
                  <td className="p-3">{t.protocol_name || "—"}</td>
                  <td className="p-3">
                    {t.suggested_code ? <span className={`chip-code text-xs ${codeClasses(t.suggested_code)}`}>{t.suggested_code}</span> : "—"}
                  </td>
                  <td className="p-3">
                    {t.assigned_code ? <span className={`chip-code text-xs ${codeClasses(t.assigned_code)}`}>{t.assigned_code}</span> : "—"}
                    {t.code_modified && <span className="ml-2 text-[10px] font-semibold text-orange-600 uppercase">Modificato</span>}
                  </td>
                  <td className="p-3">
                    {(() => {
                      const w = waitMinutes(t.arrival);
                      if (w == null) return <span className="text-xs text-slate-400">—</span>;
                      const thr = WAIT_THRESHOLDS[t.assigned_code];
                      const exceeded = thr != null && w > thr;
                      return (
                        <span data-testid={`wait-${t.triage_id}`} className={`inline-flex items-center gap-1 font-mono text-xs px-2 py-1 rounded ${
                          exceeded ? "bg-red-100 text-red-800 border border-red-200" : "bg-slate-100 text-slate-700 border border-slate-200"
                        }`}>
                          <Clock size={12} /> {w}′
                          {exceeded && <span className="ml-0.5 text-[10px] uppercase tracking-widest">oltre</span>}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-3 text-xs text-slate-600">{t.nurse_name || t.nurse_email}</td>
                  <td className="p-3">
                    <a href={`${API}/triages/${t.triage_id}/pdf`} target="_blank" rel="noreferrer"
                       data-testid={`history-pdf-${t.triage_id}`}
                       className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900">
                      <FilePdf size={16} /> PDF
                    </a>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">Nessun triage registrato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
